/**
 * ✅ FASE 11: Monitor de Cache
 * Dashboard para visualizar status do cache e sincronização
 */

import { memo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Database, Wifi, WifiOff, HardDrive, Trash2, RefreshCw, RotateCcw, Zap, AlertCircle, ChevronDown } from 'lucide-react';
import { useCacheStats } from '@/hooks/useOfflineCache';
import { useAuthStore } from '@/stores/useAuthStore';
import { forceAppUpdate, getCurrentVersion } from '@/utils/versionCheck';
import { useChunkErrorStats } from '@/hooks/useErrorLogs';
import { toast } from 'sonner';

export const CacheMonitor = memo(() => {
  const { user } = useAuthStore();
  const stats = useCacheStats();
  const errorStats = useChunkErrorStats();
  const [swVersion, setSwVersion] = useState<string>('');
  const [showErrors, setShowErrors] = useState(false);

  // Só mostrar em DEV ou para admins
  const shouldShow = import.meta.env.DEV || user?.email?.includes('@admin');

  if (!shouldShow) return null;
  
  // Obter versão do Service Worker
  if ('serviceWorker' in navigator && !swVersion) {
    navigator.serviceWorker.ready.then(async (registration) => {
      const sw = registration.active;
      if (sw) {
        // Versão será retornada pelo SW via message
        sw.postMessage({ type: 'GET_VERSION' });
      }
    });
  }

  const formatBytes = (bytes?: number) => {
    if (!bytes) return '-';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    return mb > 1 ? `${mb.toFixed(1)}MB` : `${kb.toFixed(0)}KB`;
  };

  const storagePercent = stats.storageUsage && stats.storageQuota
    ? (stats.storageUsage / stats.storageQuota) * 100
    : 0;

  return (
    <Card className="fixed bottom-20 right-4 z-40 shadow-xl w-80">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Database className="h-4 w-4" />
            Cache Monitor
          </CardTitle>
          <div className="flex items-center gap-2">
            {stats.isOnline ? (
              <Badge className="bg-green-500 gap-1">
                <Wifi className="h-3 w-3" />
                Online
              </Badge>
            ) : (
              <Badge variant="destructive" className="gap-1">
                <WifiOff className="h-3 w-3" />
                Offline
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 text-xs">
        {/* Estatísticas do cache */}
        <div className="space-y-2">
          <h4 className="font-semibold">Cache</h4>
          <div className="grid grid-cols-2 gap-2">
            <div className="p-2 bg-muted rounded">
              <div className="text-muted-foreground">Itens em cache</div>
              <div className="text-lg font-bold">{stats.cacheSize}</div>
            </div>
            <div className="p-2 bg-muted rounded">
              <div className="text-muted-foreground">Fila de sync</div>
              <div className="text-lg font-bold text-orange-600">{stats.queueSize}</div>
            </div>
          </div>
        </div>

        {/* Armazenamento */}
        {stats.storageUsage !== undefined && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold flex items-center gap-1">
                <HardDrive className="h-3 w-3" />
                Armazenamento
              </h4>
              <span className="text-muted-foreground">
                {storagePercent.toFixed(1)}%
              </span>
            </div>
            
            {/* Barra de progresso */}
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all"
                style={{ width: `${Math.min(storagePercent, 100)}%` }}
              />
            </div>
            
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{formatBytes(stats.storageUsage)}</span>
              <span>{formatBytes(stats.storageQuota)}</span>
            </div>
          </div>
        )}

        {/* Versões */}
        <div className="space-y-2">
          <h4 className="font-semibold">Versões</h4>
          <div className="p-2 bg-muted rounded space-y-1 text-[10px]">
            <div className="flex justify-between">
              <span className="text-muted-foreground">App:</span>
              <span className="font-mono">{getCurrentVersion().slice(0, 8)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">SW:</span>
              <span className="font-mono">{swVersion || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Ações */}
        <div className="space-y-2">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={stats.refresh}
              className="flex-1 text-xs"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={stats.clearExpired}
              className="flex-1 text-xs"
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Expirados
            </Button>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              if ('serviceWorker' in navigator) {
                const registrations = await navigator.serviceWorker.getRegistrations();
                await Promise.all(registrations.map(r => r.unregister()));
                toast.success('Service Worker desregistrado');
                setTimeout(() => window.location.reload(), 1000);
              }
            }}
            className="w-full text-xs"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Limpar Service Worker
          </Button>
          
          <Button
            variant="destructive"
            size="sm"
            onClick={stats.clearAll}
            className="w-full text-xs"
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Limpar Cache
          </Button>
          
          <Button
            size="sm"
            onClick={() => {
              forceAppUpdate();
              toast.success('Forçando atualização...');
            }}
            className="w-full text-xs"
          >
            <Zap className="h-3 w-3 mr-1" />
            Forçar Atualização
          </Button>
        </div>

        {/* Monitoramento de Erros de Chunk */}
        {errorStats.data && errorStats.data.total > 0 && (
          <Collapsible open={showErrors} onOpenChange={setShowErrors}>
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between p-2 bg-destructive/10 rounded text-xs hover:bg-destructive/20 transition-colors">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-3 w-3 text-destructive" />
                  <span className="font-semibold text-destructive">
                    {errorStats.data.total} chunk errors (24h)
                  </span>
                </div>
                <ChevronDown className={`h-3 w-3 transition-transform ${showErrors ? 'rotate-180' : ''}`} />
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <div className="space-y-1 text-[10px]">
                {errorStats.data.recentErrors.map((error: any, i: number) => (
                  <div key={i} className="p-2 bg-muted rounded">
                    <div className="text-muted-foreground">
                      {new Date(error.created_at).toLocaleTimeString()}
                    </div>
                    <div className="font-mono truncate">{error.context}</div>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Informação sobre funcionamento */}
        {!stats.isOnline && stats.queueSize > 0 && (
          <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded text-xs">
            <p className="text-orange-800 dark:text-orange-200">
              📡 {stats.queueSize} operação(ões) serão sincronizadas quando voltar online
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

CacheMonitor.displayName = 'CacheMonitor';
